// Project checklist (/checklists?projectId=) — validate-able to-dos
// beside issues. Items link to a master-plan item (inheriting its end
// date and adding a visual entry on the plan drawer + a due-date
// marker on the gantt), convert into full issues, and send reminder
// emails carrying a public validate link. Reminder recipients come
// from the project's roster (BidLight users with access) plus the
// partner companies' declared contacts.
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Button, Checkbox, Dropdown, Empty, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, message } from "antd";
import { BellOutlined, DownloadOutlined, DownOutlined, ExportOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined, PlusOutlined, CheckCircleFilled, ClockCircleOutlined, CheckOutlined, UndoOutlined, EditOutlined, PaperClipOutlined } from "@ant-design/icons";
import moment from "moment";
import { url, tokenRef } from "api";
import ChecklistItemDrawer from "components/Checklist/ChecklistItemDrawer";
import ChecklistMediaPicker from "components/Checklist/ChecklistMediaPicker";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import useLastProject from "components/Coordination/useLastProject";
import useProjectAccess from "hooks/useProjectAccess";
import { issueLabel } from "helpers/artifactPrefix";
import DateRangeFilter from "components/Coordination/DateRangeFilter";
import LocationPicker from "components/Coordination/LocationPicker";
import { localDayKey, utcDayKey, inDayRange, rangeEmpty, rangeFromParams } from "helpers/dateRange";
import { usePreference } from "helpers/userPrefs";
import ActivityPicker from "components/Coordination/ActivityPicker";
import { renderPeopleOption, peopleTagPlaceholder } from "components/PeoplePicker/PeoplePicker";
import { peoplePickerOptions, flatOptions, groupLabel, userOrEmailRef, emailValue } from "components/PeoplePicker/peoplePickerOptions";
import { activityLabel, activityTitle, withActivityPaths } from "helpers/schedule/activityLabel";
import { matchOption } from "components/SearchSelect/searchSelect";

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` };
}

// Every property worth searching on, and how to read it off a row. "All
// properties" is the old behaviour; picking one narrows the box to it, so
// "DP" finds the contractor label and not every note that mentions DP.
// Date columns take YYYY, YYYY-MM or YYYY-MM-DD - the same shape the
// issues list accepts.
const DATE_COLUMNS = ["createdAt", "dueDate", "completedAt"];
const CHECKLIST_COLUMNS = [
  { value: "all", label: "All properties" },
  { value: "title", label: "Item" },
  { value: "notes", label: "Description / notes" },
  { value: "group", label: "Group" },
  { value: "reference", label: "Reference" },
  { value: "activity", label: "Activity" },
  { value: "party", label: "Partner / contractor" },
  { value: "responsible", label: "Responsible" },
  { value: "contractorLabel", label: "Contractor label" },
  { value: "status", label: "Status" },
  { value: "createdAt", label: "Created" },
  { value: "dueDate", label: "Due date" },
  { value: "completedAt", label: "Completed" },
  { value: "issue", label: "Issue" },
];
const dayKey = (v) => (v ? String(new Date(v).toISOString()).slice(0, 10) : "");
const api = async (path, options = {}) => {
  const res = await fetch(`${url}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...auth(), ...(options.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
  return body;
};

// The rows on screen as a file (owner, 2026-10-20: "add the same pdf, excel,
// csv export to checklists"). The ids go up in the table's order; the server
// re-applies the list's own scope to them (routes/checklist.js POST /export).
const EXPORT_NAMES = { pdf: "PDF", xlsx: "Excel", csv: "CSV" };
// `contractorIds`: the Contractor filter's pick - the file then names those
// contractors and no other (routes/checklist.js).
const downloadChecklist = async (projectId, ext, ids, note, contractorIds = []) => {
  const res = await fetch(`${url}/projects/${projectId}/checklist/export/${ext}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth() },
    body: JSON.stringify({ ids, note, ...(contractorIds.length ? { contractorIds } : {}) }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.err || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const m = (res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = m ? m[1] : `checklist-${new Date().toISOString().slice(0, 10)}.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  return Number(res.headers.get("X-Export-Count") || ids.length);
};

export default function ProjectChecklistPage() {
  const history = useHistory();
  const [projects, setProjects] = useState([]);
  // Remembers the last project (shared with the other coordination pages).
  const [projectId, setProjectId] = useLastProject(projects);
  const [items, setItems] = useState([]);
  const [planItems, setPlanItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // CRUD rights on this project (owner-company side + checklistAdd /
  // checklistEdit / checklistDelete; partner companies read-only - they
  // complete items through the emailed validate links).
  const { can } = useProjectAccess(projectId);

  // add form
  const [newTitle, setNewTitle] = useState("");
  const [newPlanItem, setNewPlanItem] = useState(undefined);
  const [newDue, setNewDue] = useState("");
  const [newRequireShot, setNewRequireShot] = useState(false);
  // Email the people put on the item, like a new issue does. Optional,
  // remembered per person (helpers/userPrefs): a page of quick entries
  // should not send a mail per row unless that is what the author wants.
  const [notifyPref, setNotifyPrefValue] = usePreference("checklists.notifyResponsible", true, { legacyKey: "bidlight.checklist.notifyResponsible" });
  const newNotify = !(notifyPref === false || notifyPref === 0 || notifyPref === "0");
  const setNotifyPref = (v) => setNotifyPrefValue(!!v);
  const [adding, setAdding] = useState(false);

  // Roster (partner companies + their people / contacts) for the party
  // columns, the drawer and bulk assignment.
  const [roster, setRoster] = useState([]);
  // THE CONTRACTOR (owner, 2026-10-23: "do the same contractor filter on the
  // checklists export", as on issues, shop drawings, document control and
  // contractor cost). One or several; an item is theirs when a picked
  // contractor is on it or one of its responsible people is one of theirs.
  const [contractorFilter, setContractorFilter] = useState([]);
  useEffect(() => { setContractorFilter([]); }, [projectId]);
  useEffect(() => {
    if (!projectId) { setRoster([]); return; }
    api(`/projects/${projectId}/participants/roster`).then((r) => setRoster(Array.isArray(r) ? r : [])).catch(() => setRoster([]));
  }, [projectId]);
  // Search (whole row, or one property) / today filters / status filter.
  // The two "today" filters survive in the URL so other pages can link
  // straight to them - the minutes converter does, to show what it just
  // created.
  const urlParams = new URLSearchParams(window.location.search);
  const [search, setSearch] = useState("");
  const [searchColumn, setSearchColumn] = useState("all");
  const [todayOnly, setTodayOnly] = useState(
    urlParams.get("dueToday") === "1" || urlParams.get("dueToday") === "true"
  );
  // Added today, whatever their status - the quickest way back to a batch
  // just entered or a set of items minutes just produced.
  const [createdTodayOnly, setCreatedTodayOnly] = useState(
    urlParams.get("createdToday") === "1" || urlParams.get("createdToday") === "true"
  );
  // Date ranges - the windowed form of the two buttons above, for the
  // question a single day cannot answer ("added while the MEP crew was
  // on site", "due before the close-out meeting"). Client-side like
  // every other filter here: this page holds the whole checklist, so
  // the rows to narrow are already in hand. Deep-linkable the same way
  // the today filters are.
  const [createdRange, setCreatedRange] = useState(() => rangeFromParams(urlParams, "created"));
  const [dueRange, setDueRange] = useState(() => rangeFromParams(urlParams, "due"));
  const [statusFilter, setStatusFilter] = useState("all");
  // "Hide completed" / "Hide abandoned" - both ON by default, so the list
  // opens on outstanding work. Client-side, like every other filter here,
  // and remembered in localStorage so unticking to review finished items
  // survives leaving the page. A checklist item finishes as 'complete' -
  // there is no 'resolved' state on this side, so that is what the box
  // says. The Status select overrides both, so they grey out while it is
  // set to anything but All.
  // Remembered per person (helpers/userPrefs; was one setting per browser).
  const [hidePref, setHidePref] = usePreference("checklists.hideFilters", {}, { legacyKey: "checklist_hide_filters" });
  const hideSaved = hidePref && typeof hidePref === "object" ? hidePref : {};
  const hideCompleted = hideSaved.hideCompleted ?? true;
  const hideAbandoned = hideSaved.hideAbandoned ?? true;
  const setHideCompleted = (v) => setHidePref((h) => ({ ...(h && typeof h === "object" ? h : {}), hideCompleted: v }));
  const setHideAbandoned = (v) => setHidePref((h) => ({ ...(h && typeof h === "object" ? h : {}), hideAbandoned: v }));
  // Row selection for bulk assignment
  const [selectedKeys, setSelectedKeys] = useState([]);
  // Both hold LISTS: one bulk assignment can name several contractors
  // and several people from each.
  const [bulkCompany, setBulkCompany] = useState([]);
  const [bulkUser, setBulkUser] = useState([]);
  // The schedule activity for the same batch. Linking an item to an
  // activity is what puts it on the gantt at all - it draws as a marker
  // on that activity's row on its due date - and it is also what makes
  // the item inherit the activity's end date and follow it when the
  // activity is completed or disabled. Imported items arrive with no
  // activity, so without a bulk control every one of them has to be
  // linked by hand.
  const [bulkActivity, setBulkActivity] = useState(undefined);
  const [bulkWorking, setBulkWorking] = useState(false);
  // Drawer
  const [drawerItem, setDrawerItem] = useState(null);

  // remind modal
  const [remindRow, setRemindRow] = useState(null);
  const [recipientOptions, setRecipientOptions] = useState([]);
  // Who a reminder goes to, worked out from the item rather than asked
  // for: the people responsible for it, the contacts of the contractors
  // on it, and whoever was reminded before - each with a tick, so the
  // sender adds or removes rather than typing addresses from memory.
  const [remindCandidates, setRemindCandidates] = useState([]);
  const [remindEmails, setRemindEmails] = useState([]);
  const [reminding, setReminding] = useState(false);

  useEffect(() => {
    fetch(`${url}/projects/picker`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : (rows?.projects || []);
        setProjects(list);
        setProjectId((cur) =>
          cur && list.some((p) => p._id === cur) ? cur : list[0]?._id || ""
        );
      })
      .catch(() => {});
  }, []);

  const load = () => {
    if (!projectId) return;
    setLoading(true);
    Promise.all([
      api(`/projects/${projectId}/checklist`),
      fetch(`${url}/projects/${projectId}/gantt`, { headers: auth() })
        .then((r) => (r.ok ? r.json() : { items: [] })),
    ])
      .then(([rows, feed]) => {
        setItems(Array.isArray(rows) ? rows : []);
        setPlanItems(withActivityPaths(feed.items || []).filter((it) => it.status !== "done"));
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  const addItem = async () => {
    if (!newTitle.trim()) return message.warning("Item title required");
    if (planItems.length && !newPlanItem) return message.warning("Pick the schedule activity this item belongs to");
    setAdding(true);
    try {
      await api(`/projects/${projectId}/checklist`, {
        method: "POST",
        body: JSON.stringify({
          title: newTitle,
          ganttItemId: newPlanItem || undefined,
          dueDate: newDue || undefined,
          requireScreenshot: newRequireShot,
          notifyResponsible: newNotify,
        }),
      });
      setNewTitle(""); setNewPlanItem(undefined); setNewDue(""); setNewRequireShot(false);
      load();
    } catch (e) {
      message.error(e.message);
    } finally {
      setAdding(false);
    }
  };

  // The selection's items, done or reopened in one go. Sequential on
  // purpose: each PATCH is the same call a single row made, so the
  // approval rules (a partner's completion goes to review) hold per item.
  const [bulkDoneWorking, setBulkDoneWorking] = useState(false);
  // Mark done asks for optional proof first: a note and files (photo,
  // site video, Excel / PDF / DWG). Whatever is given is linked to each
  // item's completion on its Updates thread - the same files on every
  // selected item, uploaded once through the first one's media route.
  const [doneModal, setDoneModal] = useState(false);
  const [doneNote, setDoneNote] = useState("");
  const [doneMedia, setDoneMedia] = useState([]);
  const uploadDoneMedia = async (files) => {
    const first = selectedKeys[0];
    if (!first) throw new Error("Select at least one item first");
    const fd = new FormData();
    for (const f of files) fd.append("files", f, f.name || "capture");
    const res = await fetch(`${url}/projects/${projectId}/checklist/${first}/media`, { method: "POST", headers: auth(), body: fd });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
    return body.added || [];
  };
  const bulkComplete = async (checked, extra = {}) => {
    const ids = selectedKeys.slice();
    if (!ids.length) return;
    setBulkDoneWorking(true);
    let ok = 0;
    try {
      for (const id of ids) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await api(`/projects/${projectId}/checklist/${id}`, {
            method: "PATCH",
            body: JSON.stringify({ status: checked ? "complete" : "open", ...extra }),
          });
          ok += 1;
        } catch (e) { message.error(e.message); }
      }
      message.success(`${ok} item${ok === 1 ? "" : "s"} ${checked ? "marked done" : "reopened"}`);
      setSelectedKeys([]);
      setDoneModal(false); setDoneNote(""); setDoneMedia([]);
      load();
    } finally { setBulkDoneWorking(false); }
  };

  const convert = async (row) => {
    try {
      const res = await api(`/projects/${projectId}/checklist/${row._id}/convert`, { method: "POST" });
      message.success(`Issue ${issueLabel(res)} created`);
      load();
    } catch (e) { message.error(e.message); }
  };

  // Recipients: roster (BidLight users with project access) + partner
  // declared contacts, deduped by email.
  const openRemind = async (row) => {
    setRemindRow(row);
    setRemindCandidates([]);
    setRemindEmails([]);
    try {
      const [roster, participants] = await Promise.all([
        api(`/projects/${projectId}/participants/roster`),
        api(`/projects/${projectId}/participants`),
      ]);
      // The recipients this item names, in the order a sender thinks of
      // them: the responsible people, then the contacts of each
      // contractor on it (ticked only when that contractor has nobody
      // named), then anyone reminded before.
      const cands = new Map();
      const addCand = (email, name, company, reason, checked) => {
        const key = String(email || "").trim().toLowerCase();
        if (!key) return;
        const have = cands.get(key);
        if (have) { if (checked) have.checked = true; if (!have.reason.includes(reason)) have.reason.push(reason); return; }
        cands.set(key, { email: key, name: name || "", company: company || "", reason: [reason], checked: !!checked });
      };
      const rosterUserById = new Map();
      const rosterGroupById = new Map();
      for (const g of roster || []) {
        rosterGroupById.set(String(g.companyId), g);
        for (const u of g.users || []) if (u.userId) rosterUserById.set(String(u.userId), { ...u, companyName: g.companyName });
      }
      const people = Array.isArray(row.responsibleUsers) ? row.responsibleUsers : [];
      const companyIds = Array.isArray(row.responsibleCompanyIds) && row.responsibleCompanyIds.length
        ? row.responsibleCompanyIds.map(String) : (row.responsibleCompanyId ? [String(row.responsibleCompanyId)] : []);
      const namedCompanies = new Set();
      for (const u of people) {
        const known = rosterUserById.get(String(u.userId || u));
        const email = u.email || (known && known.email);
        const company = companyNameOf(u.companyId) || (known && known.companyName) || "";
        if (u.companyId) namedCompanies.add(String(u.companyId));
        addCand(email, u.name || (known && known.name), company, "Responsible", true);
      }
      for (const cid of companyIds) {
        const g = rosterGroupById.get(cid);
        if (!g) continue;
        const tick = !namedCompanies.has(cid);
        for (const u of g.users || []) addCand(u.email, u.name, g.companyName, `${g.companyName || "Contractor"} contact`, tick);
      }
      for (const r of row.recipients || []) addCand(r.email, r.name, "", "Reminded before", true);
      const list = [...cands.values()];
      setRemindCandidates(list);
      setRemindEmails(list.filter((c) => c.checked).map((c) => c.email));
      // The shared picker's groups (components/PeoplePicker), plus partner
      // rows still at "invited", which are not on the roster yet.
      const groups = peoplePickerOptions(roster, { personValue: emailValue });
      const known = new Set(flatOptions(groups).map((o) => o.value));
      const invited = [];
      for (const p of participants || []) {
        for (const u of p.users || []) {
          const e = String(u.email || "").toLowerCase();
          if (e && !known.has(e)) {
            known.add(e);
            invited.push({ value: e, label: `${u.name || u.email} — ${p.companyName || "partner"}${u.userId ? "" : " (invited)"}`, search: `${u.name || ""} ${e} ${p.companyName || ""}`.toLowerCase() });
          }
        }
      }
      setRecipientOptions(invited.length ? [...groups, { label: "Invited partners", key: "x:invited", options: invited }] : groups);
    } catch (e) {
      setRecipientOptions([]);
      // Without the rosters the item itself still names its people.
      const list = (Array.isArray(row.responsibleUsers) ? row.responsibleUsers : [])
        .filter((u) => u.email)
        .map((u) => ({ email: String(u.email).toLowerCase(), name: u.name || "", company: companyNameOf(u.companyId), reason: ["Responsible"], checked: true }));
      for (const r of row.recipients || []) if (r.email && !list.some((c) => c.email === String(r.email).toLowerCase())) list.push({ email: String(r.email).toLowerCase(), name: r.name || "", company: "", reason: ["Reminded before"], checked: true });
      setRemindCandidates(list);
      setRemindEmails(list.map((c) => c.email));
    }
  };

  const sendReminders = async () => {
    if (!remindEmails.length) return message.warning("Pick at least one recipient");
    setReminding(true);
    try {
      const res = await api(`/projects/${projectId}/checklist/${remindRow._id}/remind`, {
        method: "POST",
        body: JSON.stringify({ emails: remindEmails }),
      });
      message.success(`Reminder sent to ${res.sent.length} recipient(s)`);
      setRemindRow(null);
      load();
    } catch (e) {
      message.error(e.message);
    } finally {
      setReminding(false);
    }
  };


  const companyOptions = useMemo(() => roster.map((g) => ({ value: g.companyId, label: groupLabel(g) })), [roster]);
  const peopleOf = (companyId) => {
    const g = roster.find((x) => String(x.companyId) === String(companyId));
    return (g?.users || []).map((u) => ({ value: u.userId || `email:${u.email}`, label: `${u.name || u.email}${u.email ? ` <${u.email}>` : ""}${u.userId ? "" : " · no account yet"}`, name: u.name || u.email }));
  };
  const companyNameOf = (id) => roster.find((g) => String(g.companyId) === String(id))?.companyName || "";
  const tradesOfCompany = (id) => {
    const g = roster.find((x) => String(x.companyId) === String(id));
    return (g && ((g.tradeLabels && g.tradeLabels.length ? g.tradeLabels : g.trades) || [])) || [];
  };

  // ── several contractors, several people from each ──────────────────────
  //
  // The same shape as the create-issue responsible picker: ONE list
  // grouped by "company — trades", so picking people from two different
  // contractors is just picking two entries, and the partner box is a
  // plain multi-select beside it.
  //
  // Rows written before this was a list carry only the singular fields, so
  // every read goes through these two, never through row.responsibleUserId
  // directly. An old row keeps working and becomes a one-entry list the
  // first time anyone edits it.
  const companyIdsOf = (r) => (
    Array.isArray(r?.responsibleCompanyIds) && r.responsibleCompanyIds.length
      ? r.responsibleCompanyIds.map(String)
      : (r?.responsibleCompanyId ? [String(r.responsibleCompanyId)] : [])
  );
  const userValuesOf = (r) => (
    Array.isArray(r?.responsibleUsers) && r.responsibleUsers.length
      ? r.responsibleUsers.map((u) => String(u.userId || u)).filter(Boolean)
      : (r?.responsibleUserId ? [String(r.responsibleUserId)] : [])
  );
  const userNamesOf = (r) => (
    Array.isArray(r?.responsibleUsers) && r.responsibleUsers.length
      ? r.responsibleUsers.map((u) => u.name || u.email).filter(Boolean)
      : (r?.responsibleUserName ? [r.responsibleUserName] : [])
  );

  // People grouped by company. Narrowed to the contractors chosen on the
  // row when there are any - that is what the partner box is FOR - and the
  // whole roster when there are none, so the person box is never a dead
  // control that makes you guess which box to fill first.
  const groupedPeople = (companyIds) => {
    const wanted = (companyIds || []).map(String);
    const groups = roster.filter((g) => !wanted.length || wanted.includes(String(g.companyId)));
    // The shared picker's options (components/PeoplePicker, owner
    // 2026-10-15): "company - trade" headers, people tagged by kind; a
    // partner executive greyed out - a reader is never responsible. Values
    // as before: a user id, or `email:<addr>` (the checklist route
    // provisions a roster contact, as it always has).
    return peoplePickerOptions(groups, { personValue: userOrEmailRef, executives: "disable" })
      .map((g) => ({
        ...g,
        options: g.options.map((o) => ({
          ...o,
          companyId: String(o.group.companyId),
          name: (o.person && o.person.name) || o.label,
        })),
      }));
  };
  // Send the company each person was picked under, so the item records
  // "this person, for that contractor" rather than making the server guess.
  const peoplePayload = (values, companyIds) => {
    const flat = groupedPeople(companyIds).flatMap((g) => g.options);
    return (values || []).map((v) => {
      const o = flat.find((x) => x.value === v);
      return { userId: v, name: o?.name || "", companyId: o?.companyId || "" };
    });
  };
  const patchItem = async (row, body) => {
    try { await api(`/projects/${projectId}/checklist/${row._id}`, { method: "PATCH", body: JSON.stringify(body) }); load(); }
    catch (e) { message.error(e.message); }
  };

  // Editing an item after it was created: its text, notes, due date,
  // schedule activity and who is responsible, in one form. The drawer
  // could already re-point a single partner and person; a created item
  // whose title was mistyped or whose date moved had no way to change
  // (owner, 2026-09-14). The same PATCH the bulk bar uses, so the
  // responsible lists keep the "this person, for that contractor" shape.
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", notes: "", dueDate: "", ganttItemId: "", companies: [], users: [], location: {} });
  const [editSaving, setEditSaving] = useState(false);
  const openEdit = (row) => {
    setEditItem(row);
    setEditForm({
      title: row.title || "",
      notes: row.notes || "",
      dueDate: row.dueDate ? String(row.dueDate).slice(0, 10) : "",
      ganttItemId: String(row.ganttItemId?._id || row.ganttItemId || ""),
      companies: companyIdsOf(row),
      users: userValuesOf(row),
      // A picked drawn area is shown by its id; anything else as its text.
      location: {
        level: (row.location && row.location.level) || "",
        area: row.areaId ? `area:${row.areaId}` : ((row.location && row.location.area) || ""),
        grid: (row.location && row.location.grid) || "",
      },
    });
  };
  const saveEdit = async () => {
    if (!editItem) return;
    const title = editForm.title.trim();
    if (!title) { message.warning("The item needs a title"); return; }
    setEditSaving(true);
    try {
      const body = {
        title,
        notes: editForm.notes,
        // An empty date clears it; the server treats "" as "no due date".
        dueDate: editForm.dueDate || "",
        // An empty activity unlinks the item from the schedule.
        ganttItemId: editForm.ganttItemId || "",
        responsibleCompanyIds: editForm.companies,
        responsibleUsers: peoplePayload(editForm.users, editForm.companies),
        location: editForm.location || {},
      };
      await api(`/projects/${projectId}/checklist/${editItem._id}`, { method: "PATCH", body: JSON.stringify(body) });
      message.success("Item updated");
      setEditItem(null);
      load();
    } catch (e) { message.error(e.message); }
    finally { setEditSaving(false); }
  };
  const bulkAssign = async (ids) => {
    const targets = ids && ids.length ? ids : selectedKeys;
    if (!targets.length) return;
    const anyCompany = Array.isArray(bulkCompany) ? bulkCompany.length : !!bulkCompany;
    const anyUser = Array.isArray(bulkUser) ? bulkUser.length : !!bulkUser;
    if (!anyCompany && !anyUser && bulkActivity === undefined) {
      return message.warning("Pick a partner, a person or a schedule activity");
    }
    setBulkWorking(true);
    try {
      const body = {};
      // Both arrive as lists now, so one bulk assignment can put two
      // contractors and their people onto every ticked item at once.
      const companies = Array.isArray(bulkCompany) ? bulkCompany : (bulkCompany ? [bulkCompany] : []);
      const users = Array.isArray(bulkUser) ? bulkUser : (bulkUser ? [bulkUser] : []);
      if (anyCompany || anyUser) {
        if (bulkCompany !== undefined) body.responsibleCompanyIds = companies;
        if (bulkUser !== undefined) body.responsibleUsers = peoplePayload(users, companies);
      }
      // "" is a deliberate UNLINK, so it is sent; undefined means the
      // activity box was not touched and must not clear anybody's link.
      // The route already gives an item with no due date the activity's
      // end date, so there is nothing to ask for here.
      if (bulkActivity !== undefined) body.ganttItemId = bulkActivity || "";
      for (const id of targets) await api(`/projects/${projectId}/checklist/${id}`, { method: "PATCH", body: JSON.stringify(body) });
      message.success(`${targets.length} item(s) assigned`);
      setSelectedKeys([]); setBulkCompany([]); setBulkUser([]); setBulkActivity(undefined); setAssignAllOpen(false); load();
    } catch (e) { message.error(e.message); }
    finally { setBulkWorking(false); }
  };
  const [assignAllOpen, setAssignAllOpen] = useState(false);
  // What one property reads as, for the column-scoped search. Dates are
  // compared as YYYY-MM-DD text so a prefix ("2026-08") narrows to a month.
  const searchableOf = (r, col) => {
    switch (col) {
      case "title": return [r.title];
      case "notes": return [r.notes, r.answer];
      case "group": return [r.group];
      case "reference": return [r.reference, r.externalRef];
      // Search reaches the ANCESTORS too: the label now shows a level, and
      // a level you can see but cannot search for is worse than one you
      // never saw.
      case "activity": return [r.ganttItemId?.name, ...(r.ganttItemId?.path || [])];
      // An item owned by two contractors belongs under BOTH of them.
      case "party": return [...companyIdsOf(r).map(companyNameOf), r.contractorLabel];
      case "responsible": return [r.responsibleUserName, r.responsibleUserEmail, r.currentAssignee?.name, r.currentAssignee?.email, r.completedBy?.startsWith("email:") ? r.completedBy.slice(6) : ""];
      case "contractorLabel": return [r.contractorLabel];
      case "status": return [r.status];
      case "issue": return [r.issueId?.displayId ? issueLabel(r.issueId) : "", r.issueId?.title];
      case "createdAt": return [dayKey(r.createdAt)];
      case "dueDate": return [dayKey(r.dueDate)];
      case "completedAt": return [dayKey(r.completedAt)];
      default: return [];
    }
  };

  const contractorChoices = roster.filter((g) => g && g.role !== "owner" && g.companyId)
    .map((g) => ({ value: String(g.companyId), label: g.companyName || String(g.companyId) }));
  // The picked contractors an item is under: on the item itself, or through
  // a responsible person - their company on the row, or their address on the
  // contractor's roster (a contact with no account yet).
  const pickedContractorsOf = (r) => {
    if (!contractorFilter.length) return [];
    const on = new Set(companyIdsOf(r));
    const people = Array.isArray(r?.responsibleUsers) && r.responsibleUsers.length ? r.responsibleUsers
      : (r?.responsibleUserEmail || r?.responsibleUserId ? [{ userId: r.responsibleUserId, email: r.responsibleUserEmail }] : []);
    for (const u of people) if (u && u.companyId) on.add(String(u.companyId));
    const mails = new Set(people.map((u) => String((u && u.email) || "").toLowerCase()).filter(Boolean));
    const ids = new Set(people.map((u) => String((u && (u.userId || u)) || "")).filter(Boolean));
    return contractorFilter.filter((cid) => on.has(cid) || (roster.find((g) => String(g.companyId) === cid)?.users || [])
      .some((u) => (u.email && mails.has(String(u.email).toLowerCase())) || (u.userId && ids.has(String(u.userId)))));
  };
  // Visible rows: search (the whole row, or the one property picked),
  // Created today (added today), Due today (due today or overdue and not
  // complete), status.
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const end = new Date(); end.setHours(23, 59, 59, 999);
    const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
    return items.filter((r) => {
      if (statusFilter === "open" && r.status === "complete") return false;
      if (statusFilter === "done" && r.status !== "complete") return false;
      // Asking for a status explicitly beats the two hide boxes, so
      // "Done" still lists done items rather than nothing.
      if (statusFilter === "all") {
        if (hideCompleted && r.status === "complete") return false;
        if (hideAbandoned && r.status === "abandoned") return false;
      }
      if (todayOnly && !(r.dueDate && new Date(r.dueDate) <= end && r.status !== "complete")) return false;
      if (createdTodayOnly && !(r.createdAt && new Date(r.createdAt) >= dayStart)) return false;
      // createdAt is an instant, so it is read in the reader's day;
      // dueDate is date-only (midnight UTC), so it is read as the
      // calendar day it names. See helpers/dateRange.js.
      if (!inDayRange(localDayKey(r.createdAt), createdRange.from, createdRange.to)) return false;
      if (!inDayRange(utcDayKey(r.dueDate), dueRange.from, dueRange.to)) return false;
      if (contractorFilter.length && !pickedContractorsOf(r).length) return false;
      if (!q) return true;
      if (searchColumn !== "all") {
        const vals = searchableOf(r, searchColumn).filter(Boolean).map((v) => String(v).toLowerCase());
        // A date column matches on prefix: 2026, 2026-08, 2026-08-20.
        return DATE_COLUMNS.includes(searchColumn)
          ? vals.some((v) => v.startsWith(q))
          : vals.some((v) => v.includes(q));
      }
      const hay = [r.title, r.notes, r.group, r.reference, r.ganttItemId?.name, ...(r.ganttItemId?.path || []),
        ...companyIdsOf(r).map(companyNameOf), ...userNamesOf(r),
        ...(r.responsibleUsers || []).map((u) => u.email),
        r.responsibleUserName, r.responsibleUserEmail, r.contractorLabel, r.currentAssignee?.name, r.currentAssignee?.email, r.status, dayKey(r.createdAt), dayKey(r.dueDate)]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, searchColumn, todayOnly, createdTodayOnly, createdRange.from, createdRange.to, dueRange.from, dueRange.to, statusFilter, hideCompleted, hideAbandoned, roster, contractorFilter]);
  // The filters in words, for the file's first line: a slice of the
  // checklist must not read as all of it.
  const exportNote = () => {
    const bits = [];
    if (search.trim()) bits.push(searchColumn === "all" ? `search "${search.trim()}"` : `${(CHECKLIST_COLUMNS.find((c) => c.value === searchColumn) || {}).label || searchColumn} "${search.trim()}"`);
    if (createdTodayOnly) bits.push("created today");
    if (todayOnly) bits.push("due today or overdue");
    if (!rangeEmpty(createdRange)) bits.push(`created ${createdRange.from || "…"} to ${createdRange.to || "…"}`);
    if (!rangeEmpty(dueRange)) bits.push(`due ${dueRange.from || "…"} to ${dueRange.to || "…"}`);
    if (statusFilter === "open") bits.push("not done");
    if (statusFilter === "done") bits.push("done");
    if (statusFilter === "all" && hideCompleted) bits.push("completed hidden");
    if (statusFilter === "all" && hideAbandoned) bits.push("abandoned hidden");
    if (contractorFilter.length) bits.push(`contractor${contractorFilter.length === 1 ? "" : "s"} ${contractorFilter.map((c) => companyNameOf(c) || "one more").join(", ")}`);
    return bits.length ? `${visibleItems.length} of ${items.length} items: ${bits.join(", ")}` : "";
  };
  const [exporting, setExporting] = useState(null);
  const onExport = async ({ key: ext }) => {
    setExporting(ext);
    try {
      const n = await downloadChecklist(projectId, ext, visibleItems.map((i) => i._id), exportNote(), contractorFilter);
      message.success(`${EXPORT_NAMES[ext]} downloaded - ${n} item${n === 1 ? "" : "s"}`);
    } catch (e) {
      message.error(e.message || `${EXPORT_NAMES[ext]} export failed`);
    } finally {
      setExporting(null);
    }
  };
  const liveDrawerItem = drawerItem ? items.find((i) => i._id === drawerItem._id) || drawerItem : null;

  // ONE checkbox per row. The row used to carry two - the selection box
  // for bulk assignment and a "done" box that PATCHed the item - side by
  // side with no header on either, and the owner could not tell them
  // apart (2026-09-14). The selection box is the only one now; marking
  // done, reopening and assigning are all bulk actions on the selection
  // bar above the table, and this column SHOWS where an item stands.
  const columns = [
    {
      title: "Done", width: 64, align: "center",
      render: (_, r) => {
        if (r.status === "complete") {
          return <Tooltip title="Done"><CheckCircleFilled style={{ color: "#3f9b66", fontSize: 16 }} /></Tooltip>;
        }
        if (r.status === "pending_review") {
          return <Tooltip title="Marked complete — waiting on the project owner's approval"><ClockCircleOutlined style={{ color: "#d48806", fontSize: 15 }} /></Tooltip>;
        }
        if (r.status === "abandoned") return <span style={{ color: "#bbb" }}>—</span>;
        return <Tooltip title="Not done - tick the row and use Mark done"><span style={{ display: "inline-block", width: 14, height: 14, borderRadius: 7, border: "1.5px solid #c9ced3" }} /></Tooltip>;
      },
    },
    {
      title: "Item", dataIndex: "title",
      render: (v, r) => (
        <span style={{ textDecoration: r.status === "complete" || r.status === "abandoned" ? "line-through" : "none", color: r.status === "abandoned" ? "#999" : undefined }}>
          <a onClick={() => setDrawerItem(r)} style={{ color: "inherit" }} title="Open this item (updates, screenshots, hand-off)">{v}</a>
          {r.status === "abandoned" && <Tooltip title={r.abandonedByActivity ? "Abandoned because its schedule activity is disabled" : "Abandoned"}><Tag style={{ marginLeft: 6 }}>abandoned</Tag></Tooltip>}
          {r.group && <Tag style={{ marginLeft: 6, fontSize: 10 }}>{r.group}</Tag>}
          {r.status === "pending_review" ? (
            <Tooltip title={`Marked complete${r.completedBy?.startsWith("email:") ? ` via link by ${r.completedBy.slice(6)}` : ""} — waiting on the project owner's approval in the Review Center`}>
              <Tag color="gold" style={{ marginLeft: 8 }}>awaiting approval</Tag>
            </Tooltip>
          ) : (
            r.completedBy?.startsWith("email:") && (
              <Tooltip title={`Validated via link by ${r.completedBy.slice(6)}`}>
                <Tag color="green" style={{ marginLeft: 8 }}>validated</Tag>
              </Tooltip>
            )
          )}
        </span>
      ),
    },
    {
      title: "Due", dataIndex: "dueDate", width: 110,
      render: (v, r) => {
        if (!v) return "—";
        const overdue = r.status === "open" && new Date(v) < new Date();
        return <Tag color={overdue ? "red" : "default"}>{String(v).slice(0, 10)}</Tag>;
      },
    },
    {
      title: "Created", dataIndex: "createdAt", width: 110,
      render: (v) => (v ? (
        <Tooltip title={moment(v).format("MMMM D, YYYY h:mm A")}>
          <span style={{ color: "#666" }}>{moment(v).format("MMM D, YYYY")}</span>
        </Tooltip>
      ) : "—"),
    },
    {
      title: "Activity", dataIndex: "ganttItemId", width: 200,
      // 200px of column for a path: the label truncates to root + last two
      // and the whole thing is on the tag's title, so hovering still gives it.
      render: (v) => (v ? <Tag color="blue" title={activityTitle(v)}>{activityLabel(v)}</Tag> : "—"),
    },
    {
      title: "Partner / contractor", key: "party", width: 250,
      render: (_, r) => can.checklistEdit ? (
        <Select filterOption={matchOption} size="small" mode="multiple" allowClear showSearch optionFilterProp="label"
          style={{ width: "100%" }}
          // One name is always shown, with the rest behind a "+N" that
          // names them on hover. "responsive" collapsed EVERYTHING to
          // "+2 ..." at this column width, which tells the reader that
          // somebody is on it but not who - the one thing the column is
          // there to say.
          maxTagCount={1}
          maxTagPlaceholder={(rest) => (
            <Tooltip title={rest.map((x) => x.label).join(", ")}>+{rest.length}</Tooltip>
          )}
          placeholder={r.contractorLabel ? `${r.contractorLabel} (label)` : "Partners"}
          value={companyIdsOf(r)} options={companyOptions}
          onChange={(vals) => {
            // Dropping a contractor drops the people who were on the item
            // FOR that contractor, and nobody else. Leaving them behind
            // would show a person under a partner who is no longer on it.
            const keep = (r.responsibleUsers || [])
              .filter((u) => !u.companyId || vals.includes(String(u.companyId)))
              .map((u) => ({ userId: String(u.userId), name: u.name || "", companyId: String(u.companyId || "") }));
            patchItem(r, { responsibleCompanyIds: vals, responsibleUsers: keep });
          }} />
      ) : (
        // Each partner with its trades beneath (owner, 2026-10-16).
        (companyIdsOf(r).filter((id) => companyNameOf(id)).length
          ? companyIdsOf(r).filter((id) => companyNameOf(id)).map((id) => (
            <div key={id}><PartnerWithTrades name={companyNameOf(id)} trades={tradesOfCompany(id)} /></div>
          ))
          : null)
        || (r.contractorLabel ? <Tag color="gold">{r.contractorLabel}</Tag> : "—")
      ),
    },
    {
      title: "Responsible", key: "person", width: 250,
      render: (_, r) => can.checklistEdit ? (
        <Select filterOption={matchOption} size="small" mode="multiple" allowClear showSearch optionFilterProp="search"
          style={{ width: "100%" }}
          maxTagCount={1}
          maxTagPlaceholder={(rest) => (
            <Tooltip title={rest.map((x) => x.label).join(", ")}>+{rest.length}</Tooltip>
          )}
          placeholder={companyIdsOf(r).length ? "People" : "Anyone on the project"}
          value={userValuesOf(r)} options={groupedPeople(companyIdsOf(r))} optionRender={renderPeopleOption} maxTagPlaceholder={peopleTagPlaceholder(groupedPeople(companyIdsOf(r)))}
          onChange={(vals) => patchItem(r, { responsibleUsers: peoplePayload(vals, companyIdsOf(r)) })} />
      ) : (userNamesOf(r).join(", ") || r.currentAssignee?.name || "—"),
    },
    {
      title: "Issue", dataIndex: "issueId", width: 110,
      render: (v) =>
        v ? (
          <a onClick={() => history.push(`/issue-viewer/${v._id || v}`)}>
            {v.displayId ? `${issueLabel(v)}` : "open"}
          </a>
        ) : ("—"),
    },
    {
      // The item's conversation: what link recipients posted from the
      // public page and what a reviewer said. The link ALWAYS opens the
      // thread, empty or not - an empty thread is where the first update
      // gets written, and a column that is sometimes a link reads as
      // broken (owner, 2026-09-14). The icon is the public page, a
      // genuinely different place: what the emailed recipients see.
      title: "Updates", width: 120,
      render: (_, r) => {
        const n = (r.thread || []).length;
        const files = (r.thread || []).reduce((acc, t) => acc + ((t.attachments || []).length || (t.screenshotUrl ? 1 : 0)), 0);
        return (
          <Space size={4}>
            <a onClick={() => setDrawerItem(r)} title={n ? "Open the updates" : "No updates yet - open to write the first"}
               style={n ? undefined : { color: "#8c8c8c" }}>
              {n === 0 ? "No updates" : `${n} update${n === 1 ? "" : "s"}`}
            </a>
            {files > 0 && (
              <Tooltip title={`${files} file${files === 1 ? "" : "s"} attached to the updates (photos, video, documents)`}>
                <span style={{ fontSize: 11, color: "#6B7280" }}><PaperClipOutlined /> {files}</span>
              </Tooltip>
            )}
            <a href={`/checklist/validate/${r.token}`} target="_blank" rel="noreferrer" title="Public page (what link recipients see)" style={{ color: "#999" }}><ExportOutlined /></a>
          </Space>
        );
      },
    },
    {
      title: "Reminders", width: 130,
      render: (_, r) => (
        <span style={{ fontSize: 12, color: "#888" }}>
          {r.recipients?.length ? `${r.recipients.length} recipient(s)` : "—"}
          {r.remindersSentAt?.length ? ` · ${r.remindersSentAt.length} sent` : ""}
        </span>
      ),
    },
    {
      title: "", width: 210,
      render: (_, r) => (
        <Space size={4}>
          {can.checklistEdit && (
            <Tooltip title="Edit the title, notes, due date, activity or who is responsible">
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
            </Tooltip>
          )}
          {can.checklistEdit && (
            <Button size="small" icon={<BellOutlined />} onClick={() => openRemind(r)}>
              Remind
            </Button>
          )}
          {!r.issueId && can.checklistEdit && can.issueCreate && (
            <Popconfirm title="Convert this item into an issue?" onConfirm={() => convert(r)}>
              <Button size="small" icon={<ExportOutlined />}>To issue</Button>
            </Popconfirm>
          )}
          {can.checklistDelete && (
            <Popconfirm title="Delete this item?" onConfirm={async () => {
              await api(`/projects/${projectId}/checklist/${r._id}`, { method: "DELETE" });
              load();
            }}>
              <Button size="small" danger type="text">×</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, fontFamily: "Inter, system-ui, sans-serif", maxWidth: 1200, margin: "0 auto" }}>
      <CoordinationBreadcrumbs current="Checklists" />
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Checklists</h2>
        <Select filterOption={matchOption} style={{ minWidth: 260 }} showSearch optionFilterProp="label"
                placeholder="Project" value={projectId || undefined} onChange={setProjectId}
                options={projects.map((p) => ({ value: p._id, label: p.title || "Untitled" }))} />
        {can.checklistAdd && (
          <Button onClick={() => history.push(`/checklists/batch${projectId ? `?projectId=${projectId}` : ""}`)}
                  title="Type or paste a whole list of items at once">
            Bulk entry
          </Button>
        )}
      </div>

      <Alert type="info" showIcon style={{ marginBottom: 12 }}
             message="Items linked to a schedule activity inherit its end date, appear in the schedule drawer, and render as due-date markers on the gantt. Convert an item to a full issue, or send validate links so a partner contact or project user can mark it complete." />

      {can.checklistAdd && (
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <Input placeholder="New checklist item…" value={newTitle} style={{ flex: 1, minWidth: 240 }}
               onChange={(e) => setNewTitle(e.target.value)} onPressEnter={addItem} />
        <ActivityPicker items={planItems} placeholder={planItems.length ? "Schedule activity or category (required)" : "Link to schedule activity"}
                style={{ minWidth: 300 }} value={newPlanItem} onChange={(v) => setNewPlanItem(v || undefined)}
                status={planItems.length && !newPlanItem && newTitle.trim() ? "warning" : undefined} />
        <input type="date" value={newDue}
               style={{ border: "1px solid #d9d9d9", borderRadius: 6, padding: "3px 8px" }}
               onChange={(e) => setNewDue(e.target.value)} />
        <Tooltip title="Nobody (partner, supervisor or owner) can mark this item complete until a screenshot of the work is attached - the update is blocked on save.">
          <Checkbox checked={newRequireShot} onChange={(e) => setNewRequireShot(e.target.checked)}>Require screenshot</Checkbox>
          <Tooltip title="Send the responsible people an email about the new item, the way a new issue does. They are in the daily digest either way.">
            <Checkbox checked={newNotify} onChange={(e) => setNotifyPref(e.target.checked)}>Email the responsible</Checkbox>
          </Tooltip>
        </Tooltip>
        <Button type="primary" icon={<PlusOutlined />} loading={adding}
                disabled={!projectId} onClick={addItem}>
          Add
        </Button>
      </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
        <Input.Search allowClear
                      placeholder={searchColumn === "all"
                        ? "Search items (title, group, activity, partner, person, label…)"
                        : DATE_COLUMNS.includes(searchColumn)
                          ? "YYYY, YYYY-MM or YYYY-MM-DD"
                          : `Search ${(CHECKLIST_COLUMNS.find((c) => c.value === searchColumn) || {}).label}`}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)} style={{ width: 340 }} />
        <Select value={searchColumn} onChange={setSearchColumn} style={{ width: 170 }}
                options={CHECKLIST_COLUMNS} title="Search one property instead of the whole row" />
        <Button type={createdTodayOnly ? "primary" : "default"} onClick={() => setCreatedTodayOnly((v) => !v)}
                title="Added today, whatever their status">Created today</Button>
        <Button type={todayOnly ? "primary" : "default"} onClick={() => setTodayOnly((v) => !v)} title="Due today or overdue, not complete">Due today</Button>
        <DateRangeFilter
          label="Created"
          value={createdRange}
          onChange={setCreatedRange}
          title="Added between these two days, inclusive. Leave either end empty for an open window."
        />
        <DateRangeFilter
          label="Due"
          value={dueRange}
          onChange={setDueRange}
          title="Due between these two days, inclusive. Items with no due date are left out."
        />
        {contractorChoices.length ? (
          <Select filterOption={matchOption} mode="multiple" allowClear showSearch optionFilterProp="label"
                  maxTagCount="responsive" placeholder="Contractor" aria-label="Checklist contractor filter"
                  style={{ minWidth: 180 }} value={contractorFilter} onChange={(v) => setContractorFilter(v || [])}
                  options={contractorChoices} />
        ) : null}
        <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 130 }}
                options={[{ value: "all", label: "All" }, { value: "open", label: "Not done" }, { value: "done", label: "Done" }]} />
        <Tooltip title={statusFilter !== "all"
          ? "The Status filter decides what is listed, so these are ignored while it is set"
          : "Hide completed items - the checklist equivalent of a resolved issue. On by default"}>
          <Checkbox checked={hideCompleted} disabled={statusFilter !== "all"}
                    onChange={(e) => setHideCompleted(e.target.checked)}
                    style={{ whiteSpace: "nowrap" }}>Hide completed</Checkbox>
        </Tooltip>
        <Tooltip title={statusFilter !== "all"
          ? "The Status filter decides what is listed, so these are ignored while it is set"
          : "Hide abandoned items, including those abandoned because their schedule activity is disabled. On by default"}>
          <Checkbox checked={hideAbandoned} disabled={statusFilter !== "all"}
                    onChange={(e) => setHideAbandoned(e.target.checked)}
                    style={{ whiteSpace: "nowrap" }}>Hide abandoned</Checkbox>
        </Tooltip>
        {(search || todayOnly || createdTodayOnly || !rangeEmpty(createdRange) || !rangeEmpty(dueRange) || statusFilter !== "all" || searchColumn !== "all" || !hideCompleted || !hideAbandoned || contractorFilter.length) && (
          <Button type="text" onClick={() => {
            setSearch(""); setSearchColumn("all"); setTodayOnly(false); setContractorFilter([]);
            setCreatedTodayOnly(false); setStatusFilter("all");
            setCreatedRange({ from: "", to: "" }); setDueRange({ from: "", to: "" });
            setHideCompleted(true); setHideAbandoned(true);
          }}>Clear filters</Button>
        )}
        <span style={{ color: "#888", fontSize: 12 }}>{visibleItems.length} of {items.length}</span>
        <Dropdown
          trigger={["click"]}
          disabled={!!exporting || !projectId}
          menu={{
            onClick: onExport,
            items: [
              { key: "pdf", icon: <FilePdfOutlined />, label: "PDF" },
              { key: "xlsx", icon: <FileExcelOutlined />, label: "Excel (.xlsx)" },
              { key: "csv", icon: <FileTextOutlined />, label: "CSV" },
            ],
          }}
        >
          <Button icon={<DownloadOutlined />} loading={!!exporting} title="The items shown, with these filters">
            {exporting ? `Building ${EXPORT_NAMES[exporting]}…` : "Export PDF / Excel / CSV"} <DownOutlined style={{ fontSize: 10 }} />
          </Button>
        </Dropdown>
        {can.checklistEdit && (
          <Button onClick={() => { setAssignAllOpen((v) => !v); setBulkCompany([]); setBulkUser([]); setBulkActivity(undefined); }} disabled={!visibleItems.length}
                  title="Apply a partner, responsible people and/or a schedule activity to every item shown">Assign / link ({visibleItems.length})</Button>
        )}
        {can.checklistEdit && assignAllOpen && (
          <Space wrap style={{ background: "#f5f9ff", padding: "4px 8px", borderRadius: 6 }}>
            <span style={{ fontSize: 12 }}>{visibleItems.length} shown →</span>
            <Select filterOption={matchOption} size="small" mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="Partners" style={{ width: 220 }} maxTagCount="responsive"
                    value={bulkCompany} options={companyOptions}
                    onChange={(v) => {
                      setBulkCompany(v);
                      // Keep only the people who belong to a partner still ticked.
                      setBulkUser((cur) => {
                        if (!Array.isArray(cur) || !cur.length) return cur;
                        const allowed = new Set(groupedPeople(v).flatMap((g) => g.options).map((o) => o.value));
                        return cur.filter((x) => allowed.has(x));
                      });
                    }} />
            <Select filterOption={matchOption} size="small" mode="multiple" allowClear showSearch optionFilterProp="search" placeholder={(bulkCompany || []).length ? "People" : "Anyone on the project"} style={{ width: 240 }} maxTagCount="responsive"
                    value={bulkUser} options={groupedPeople(bulkCompany)} optionRender={renderPeopleOption} maxTagPlaceholder={peopleTagPlaceholder(groupedPeople(bulkCompany))} onChange={setBulkUser} />
            <ActivityPicker size="small" items={planItems} placeholder="Schedule activity or category" style={{ width: 280, minWidth: 280 }}
                    value={bulkActivity}
                    onChange={(v) => setBulkActivity(v === undefined ? undefined : (v || ""))} />
            {bulkActivity ? (
              <span style={{ fontSize: 12, color: "#6B7280" }}>
                items with no due date take the activity's end date
              </span>
            ) : null}
            <Button size="small" type="primary" loading={bulkWorking} onClick={() => bulkAssign(visibleItems.map((i) => i._id))}>Apply to shown</Button>
            <Button size="small" onClick={() => setAssignAllOpen(false)}>Cancel</Button>
          </Space>
        )}
        {can.checklistEdit && selectedKeys.length > 0 && (
          <Space wrap style={{ marginLeft: "auto", background: "#f5f9ff", padding: "4px 8px", borderRadius: 6 }}>
            <span style={{ fontSize: 12 }}>{selectedKeys.length} selected →</span>
            <Select filterOption={matchOption} size="small" mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="Partners" style={{ width: 220 }} maxTagCount="responsive"
                    value={bulkCompany} options={companyOptions}
                    onChange={(v) => {
                      setBulkCompany(v);
                      // Keep only the people who belong to a partner still ticked.
                      setBulkUser((cur) => {
                        if (!Array.isArray(cur) || !cur.length) return cur;
                        const allowed = new Set(groupedPeople(v).flatMap((g) => g.options).map((o) => o.value));
                        return cur.filter((x) => allowed.has(x));
                      });
                    }} />
            <Select filterOption={matchOption} size="small" mode="multiple" allowClear showSearch optionFilterProp="search" placeholder={(bulkCompany || []).length ? "People" : "Anyone on the project"} style={{ width: 240 }} maxTagCount="responsive"
                    value={bulkUser} options={groupedPeople(bulkCompany)} optionRender={renderPeopleOption} maxTagPlaceholder={peopleTagPlaceholder(groupedPeople(bulkCompany))} onChange={setBulkUser} />
            <ActivityPicker size="small" items={planItems} placeholder="Schedule activity or category" style={{ width: 280, minWidth: 280 }}
                    value={bulkActivity}
                    onChange={(v) => setBulkActivity(v === undefined ? undefined : (v || ""))} />
            {bulkActivity ? (
              <span style={{ fontSize: 12, color: "#6B7280" }}>
                items with no due date take the activity's end date
              </span>
            ) : null}
            <Button size="small" type="primary" loading={bulkWorking} onClick={() => bulkAssign()}>Assign</Button>
            <Tooltip title="Mark every selected item done, with an optional note and files as proof. A partner's completion still goes to the Review Center.">
              <Button size="small" icon={<CheckOutlined />} loading={bulkDoneWorking} onClick={() => setDoneModal(true)}>Mark done</Button>
            </Tooltip>
            <Tooltip title="Put every selected item back to not done">
              <Button size="small" icon={<UndoOutlined />} loading={bulkDoneWorking} onClick={() => bulkComplete(false)}>Reopen</Button>
            </Tooltip>
            <Button size="small" onClick={() => setSelectedKeys([])}>Clear</Button>
          </Space>
        )}
      </div>

      <Table rowKey="_id" dataSource={visibleItems} columns={columns} loading={loading}
             size="small" pagination={{ pageSize: 25, hideOnSinglePage: true }}
             rowSelection={can.checklistEdit ? { selectedRowKeys: selectedKeys, onChange: setSelectedKeys } : undefined}
             locale={{ emptyText: <Empty description={items.length ? "No items match" : "No checklist items yet"} /> }} />

      <Modal
        open={doneModal}
        title={`Mark ${selectedKeys.length} item${selectedKeys.length === 1 ? "" : "s"} done`}
        okText={bulkDoneWorking ? "Saving…" : "Mark done"}
        okButtonProps={{ loading: bulkDoneWorking }}
        onOk={() => bulkComplete(true, { comment: doneNote.trim(), attachments: doneMedia })}
        onCancel={() => { setDoneModal(false); setDoneNote(""); setDoneMedia([]); }}
        destroyOnClose
      >
        <div style={{ fontSize: 12.5, color: "#555", marginBottom: 8 }}>
          Proof is optional. A note and any files you attach here - a photo, a video taken now, the Excel / PDF / DWG of the check - are linked to each item's completion and show in its Updates.
        </div>
        <Input.TextArea rows={3} value={doneNote} onChange={(e) => setDoneNote(e.target.value)} placeholder="Note (optional) - what was checked, by whom, anything left" style={{ marginBottom: 8 }} />
        <ChecklistMediaPicker upload={uploadDoneMedia} value={doneMedia} onChange={setDoneMedia} pasteActive={doneModal} block />
      </Modal>
      <ChecklistItemDrawer open={!!liveDrawerItem} onClose={() => setDrawerItem(null)} projectId={projectId}
        item={liveDrawerItem} items={items} roster={roster} planItems={planItems} can={can}
        onChanged={load} onSelectItem={(i) => setDrawerItem(i)} onRemind={(i) => openRemind(i)} onEdit={(i) => openEdit(i)} />

      <Modal
        open={!!editItem}
        title="Edit checklist item"
        okText="Save"
        confirmLoading={editSaving}
        onOk={saveEdit}
        onCancel={() => setEditItem(null)}
        destroyOnClose
        width={560}
      >
        <div style={{ display: "grid", gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Item</div>
            <Input value={editForm.title} maxLength={300} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} onPressEnter={saveEdit} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Notes</div>
            <Input.TextArea value={editForm.notes} autoSize={{ minRows: 2, maxRows: 6 }} maxLength={5000} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
          <div>
            {/* The same levels / areas / grid an issue is located by. */}
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Location (level · area · grid)</div>
            <LocationPicker projectId={projectId} value={editForm.location || {}} onChange={(loc) => setEditForm((f) => ({ ...f, location: loc }))} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Due date</div>
              <Input type="date" value={editForm.dueDate} onChange={(e) => setEditForm((f) => ({ ...f, dueDate: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Schedule activity</div>
              <ActivityPicker style={{ width: "100%" }} placeholder="None"
                      value={editForm.ganttItemId || undefined}
                      // The plan list leaves out finished activities; an item
                      // still linked to one must show it, not a bare id.
                      items={editForm.ganttItemId && !planItems.some((o) => String(o._id) === editForm.ganttItemId)
                        ? [...planItems, { _id: editForm.ganttItemId, name: `${editItem?.ganttItemId?.name || "Current activity"} (finished)`, phase: editItem?.ganttItemId?.phase || "" }]
                        : planItems}
                      onChange={(v) => setEditForm((f) => ({ ...f, ganttItemId: v || "" }))} />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Partners / contractors</div>
            <Select filterOption={matchOption} mode="multiple" allowClear showSearch optionFilterProp="label" style={{ width: "100%" }} placeholder="Any" maxTagCount="responsive"
                    value={editForm.companies} options={companyOptions}
                    onChange={(v) => setEditForm((f) => {
                      // Keep only the people who belong to a partner still ticked.
                      const allowed = new Set(groupedPeople(v).flatMap((g) => g.options).map((o) => o.value));
                      return { ...f, companies: v, users: (f.users || []).filter((x) => allowed.has(x)) };
                    })} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Responsible people</div>
            <Select filterOption={matchOption} mode="multiple" allowClear showSearch optionFilterProp="search" style={{ width: "100%" }} maxTagCount="responsive"
                    placeholder={(editForm.companies || []).length ? "People at those partners" : "Anyone on the project"}
                    value={editForm.users} options={groupedPeople(editForm.companies)} optionRender={renderPeopleOption} maxTagPlaceholder={peopleTagPlaceholder(groupedPeople(editForm.companies))}
                    onChange={(v) => setEditForm((f) => ({ ...f, users: v }))} />
          </div>
          {editItem && editItem.status !== "open" && (
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              Status is not changed here: use the row's checkbox and the bulk bar to mark done or reopen.
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title={remindRow ? `Send validate link — ${remindRow.title}` : ""}
        open={!!remindRow}
        onCancel={() => setRemindRow(null)}
        onOk={sendReminders}
        okText="Send reminders"
        confirmLoading={reminding}
        destroyOnClose
      >
        <div style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>
          Each recipient gets an email with a link to review this item and
          mark it complete — no login needed. The people on the item are
          listed and ticked; untick anyone, or add someone below.
        </div>
        {remindCandidates.length ? (
          <div style={{ border: "1px solid #EFEFEF", borderRadius: 6, padding: "6px 10px", marginBottom: 10, maxHeight: 260, overflow: "auto" }}>
            {remindCandidates.map((c) => (
              <div key={c.email} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                <Checkbox
                  checked={remindEmails.includes(c.email)}
                  onChange={(e) => setRemindEmails((prev) => (e.target.checked ? [...new Set([...prev, c.email])] : prev.filter((x) => x !== c.email)))}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600 }}>{c.name || c.email}</span>
                  {c.name ? <span style={{ color: "#8D8D8D" }}> · {c.email}</span> : null}
                  {c.company ? <span style={{ color: "#8D8D8D" }}> · {c.company}</span> : null}
                </span>
                {c.reason.map((r) => <Tag key={r} style={{ marginInlineEnd: 0 }} color={r === "Responsible" ? "blue" : r === "Reminded before" ? "default" : "cyan"}>{r}</Tag>)}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: "#8D8D8D", marginBottom: 10 }}>
            Nobody is named on this item yet. Set its responsible people, or add recipients below.
          </div>
        )}
        <Select mode="tags" style={{ width: "100%" }} placeholder="Add other recipients (project users, partner contacts, or any email)"
                value={remindEmails.filter((e) => !remindCandidates.some((c) => c.email === e))}
                onChange={(extra) => setRemindEmails((prev) => [...new Set([...prev.filter((e) => remindCandidates.some((c) => c.email === e)), ...extra.map((e) => String(e).trim().toLowerCase())])])}
                options={recipientOptions
                  .map((g) => ({ ...g, options: g.options.filter((o) => !remindCandidates.some((c) => c.email === o.value)) }))
                  .filter((g) => g.options.length)}
                optionFilterProp="search"
                optionRender={(o) => (o.data && o.data.person ? renderPeopleOption(o) : o.label)}
                tokenSeparators={[",", " "]} />
        <div style={{ fontSize: 12, color: "#8D8D8D", marginTop: 8 }}>
          {remindEmails.length} recipient{remindEmails.length === 1 ? "" : "s"} will be emailed.
        </div>
      </Modal>
    </div>
  );
}
